import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import { REFUND_REASONS, RefundTender, RefundTenderWire } from '../api';
import { PriceBasis, RefundSelection, formatCents, refundTotals } from './money';
import { COLORS } from './theme';

type Props = {
  selections: RefundSelection[];
  /** Basis of the original sale. `owner` means no money was taken, so
   *  the return is a restock and every figure here is zero. */
  basis: PriceBasis;
  reason: string;
  note: string;
  /** `none` when returning an owner withdrawal — the picker isn't
   *  shown for those, since no cash moves. */
  tender: RefundTenderWire;
  busy: boolean;
  onQtyChange: (lineIndex: number, delta: number) => void;
  onRestockChange: (lineIndex: number, restock: boolean) => void;
  onRemove: (lineIndex: number) => void;
  onReasonChange: (reason: string) => void;
  onNoteChange: (note: string) => void;
  onTenderChange: (tender: RefundTender) => void;
  onClear: () => void;
  onRefund: () => void;
};

/** The right rail in Transactions mode. Same shape as the sale cart,
 *  red instead of green, and REFUND instead of PURCHASE — a cashier
 *  should never be in doubt about which direction the money is going. */
const RefundPanel: React.FC<Props> = ({
  selections,
  basis,
  reason,
  note,
  tender,
  busy,
  onQtyChange,
  onRestockChange,
  onRemove,
  onReasonChange,
  onNoteChange,
  onTenderChange,
  onClear,
  onRefund,
}) => {
  const totals = refundTotals(selections);
  // Returning an owner withdrawal reverses the recorded cost and HST,
  // but no cash moves — so there is no "refund to" choice to make.
  const reversal = basis === 'owner';
  const empty = selections.length === 0;
  // Reason is required rather than defaulted — a defaulted reason code
  // is data nobody can trust later.
  const canRefund = !empty && reason !== '' && !busy;

  return (
    <Box
      sx={{
        width: 420,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: COLORS.dangerBg,
        borderLeft: `3px solid ${COLORS.danger}`,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UndoIcon sx={{ color: COLORS.danger }} />
          <Typography sx={{ fontWeight: 800, letterSpacing: '0.08em', color: COLORS.danger }}>
            REFUND
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={onClear}
          disabled={empty || busy}
          sx={{ color: COLORS.textMuted, minHeight: 36 }}
        >
          Clear
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2 }}>
        {empty ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: COLORS.textMuted,
              textAlign: 'center',
              px: 2,
            }}
          >
            <Typography variant="body2">
              Pick a sale, then tap the lines coming back.
            </Typography>
          </Box>
        ) : (
          selections.map((s) => (
            <RefundLineRow
              key={s.lineIndex}
              sel={s}
              busy={busy}
              onQtyChange={onQtyChange}
              onRestockChange={onRestockChange}
              onRemove={onRemove}
            />
          ))
        )}
      </Box>

      <Box sx={{ px: 2, pb: 2, pt: 1 }}>
        <TextField
          select
          fullWidth
          size="small"
          label="Reason"
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          disabled={empty || busy}
          required
          sx={{ mb: 1, backgroundColor: COLORS.surface }}
        >
          {REFUND_REASONS.map((r) => (
            <MenuItem key={r.value} value={r.value}>
              {r.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          size="small"
          label="Note (optional)"
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          disabled={empty || busy}
          inputProps={{ maxLength: 200 }}
          sx={{ mb: 1.5, backgroundColor: COLORS.surface }}
        />

        {reversal ? (
          <Box
            sx={{
              mb: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              backgroundColor: COLORS.dangerBg,
              border: `1px solid ${COLORS.danger}`,
            }}
          >
            <Typography sx={{ fontWeight: 700, color: COLORS.danger }}>
              Owner withdrawal — no money changes hands
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
              Stock goes back on the shelf and the cost and HST recorded
              when it was taken come back off. Nothing leaves the till.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: COLORS.textMuted, mb: 0.5 }}>
              Refund to
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={tender}
              onChange={(_e, v: RefundTender | null) => {
                if (v !== null) onTenderChange(v);
              }}
              disabled={empty || busy}
              sx={{
                mb: 1.5,
                '& .Mui-selected': {
                  backgroundColor: `${COLORS.danger} !important`,
                  color: '#ffffff !important',
                },
              }}
            >
              <ToggleButton value="cash">Cash — till</ToggleButton>
              <ToggleButton value="card">Card</ToggleButton>
            </ToggleButtonGroup>
          </>
        )}

        <Divider sx={{ mb: 1 }} />
        <TotalRow label="Subtotal" value={totals.subtotal} />
        <TotalRow label="HST (14%)" value={totals.tax} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mt: 0.5,
            mb: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 800, letterSpacing: '0.06em' }}>
            REFUND TOTAL
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.9rem',
              lineHeight: 1.1,
              color: COLORS.danger,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatCents(totals.total)}
          </Typography>
        </Box>

        <Button
          fullWidth
          variant="contained"
          disabled={!canRefund}
          onClick={onRefund}
          startIcon={<UndoIcon />}
          sx={{
            py: 2.5,
            fontSize: '1.25rem',
            fontWeight: 700,
            backgroundColor: COLORS.danger,
            '&:hover': { backgroundColor: '#8e1414' },
          }}
        >
          REFUND {formatCents(totals.total)}
        </Button>
        {!empty && reason === '' && (
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', color: COLORS.warnText, mt: 0.5 }}
          >
            Pick a reason to continue.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const RefundLineRow: React.FC<{
  sel: RefundSelection;
  busy: boolean;
  onQtyChange: (lineIndex: number, delta: number) => void;
  onRestockChange: (lineIndex: number, restock: boolean) => void;
  onRemove: (lineIndex: number) => void;
}> = ({ sel, busy, onQtyChange, onRestockChange, onRemove }) => (
  <Box
    sx={{
      backgroundColor: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 2,
      p: 1.5,
      mb: 1,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Typography sx={{ flex: 1, fontWeight: 600, lineHeight: 1.3 }}>
        {sel.name}
      </Typography>
      <IconButton
        size="small"
        onClick={() => onRemove(sel.lineIndex)}
        disabled={busy}
        aria-label={`Remove ${sel.name} from refund`}
        sx={{ color: COLORS.textMuted, mt: -0.5, mr: -0.5 }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>

    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mt: 0.5,
      }}
    >
      <Box>
        <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
          {formatCents(sel.unitPrice)} ea
        </Typography>
        <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
          of {sel.maxQuantity} refundable
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          onClick={() => onQtyChange(sel.lineIndex, -1)}
          disabled={busy}
          aria-label={`Decrease refund quantity of ${sel.name}`}
          sx={{ border: `1px solid ${COLORS.border}` }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <Typography
          sx={{ minWidth: 32, textAlign: 'center', fontWeight: 700, fontSize: '1.15rem' }}
        >
          {Number.isInteger(sel.quantity) ? sel.quantity : sel.quantity.toFixed(2)}
        </Typography>
        <IconButton
          onClick={() => onQtyChange(sel.lineIndex, 1)}
          // Hard-stopped at what's left to refund. The server enforces
          // the same bound; this just stops the cashier building a
          // request that's going to be rejected.
          disabled={busy || sel.quantity >= sel.maxQuantity}
          aria-label={`Increase refund quantity of ${sel.name}`}
          sx={{ border: `1px solid ${COLORS.border}` }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>

    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Tooltip
        title={
          sel.restockable
            ? ''
            : "This line has no catalog match, so there's nothing to restock against."
        }
      >
        {/* span: MUI tooltips need a non-disabled wrapper to fire. */}
        <span>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={sel.restock}
                disabled={!sel.restockable || busy}
                onChange={(e) => onRestockChange(sel.lineIndex, e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
                Return to stock
              </Typography>
            }
          />
        </span>
      </Tooltip>
      <Typography sx={{ fontWeight: 700 }}>
        {formatCents(Math.round(sel.unitPrice * sel.quantity))}
      </Typography>
    </Box>
  </Box>
);

const TotalRow: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
    <Typography sx={{ color: COLORS.textMuted }}>{label}</Typography>
    <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
      {formatCents(value)}
    </Typography>
  </Box>
);

export default RefundPanel;
